import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronDown, ChevronUp, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { BottomNav } from '../components/BottomNav';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FAQPage() {
  const navigate = useNavigate();
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${API}/support/faq`);
      setFaqs(response.data);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-nav">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center gap-3 px-4 py-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/settings')}
            className="rounded-full"
          >
            <ArrowLeft size={20} />
          </Button>
          <h1 className="font-heading font-bold text-lg">Frequently Asked Questions</h1>
        </div>
      </header>

      <main className="px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-pulse text-muted-foreground">Loading FAQs...</div>
          </div>
        ) : (
          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="glass-card overflow-hidden">
                  <button
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    className="w-full p-4 flex items-center justify-between text-left"
                    data-testid={`faq-${index}`}
                  >
                    <span className="font-medium pr-4">{faq.question}</span>
                    {openIndex === index ? (
                      <ChevronUp size={20} className="text-muted-foreground shrink-0" />
                    ) : (
                      <ChevronDown size={20} className="text-muted-foreground shrink-0" />
                    )}
                  </button>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="px-4 pb-4"
                    >
                      <p className="text-sm text-muted-foreground">{faq.answer}</p>
                    </motion.div>
                  )}
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Contact Support CTA */}
        <Card className="glass-card p-6 mt-6 text-center">
          <HelpCircle size={32} className="text-primary mx-auto mb-3" />
          <h3 className="font-heading font-semibold mb-2">Still have questions?</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Our support team is here to help you.
          </p>
          <Button
            onClick={() => navigate('/support/contact')}
            className="bg-primary hover:bg-primary/90 rounded-full"
            data-testid="contact-support-btn"
          >
            Contact Support
          </Button>
        </Card>
      </main>

      <BottomNav />
    </div>
  );
}
